/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes, useCallback } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/ColorSwatch/ColorSwatch.css';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface ColorSwatchProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'label' | 'size'> {
  name?: string;
  label?: string;
  groupAriaLabel?: string;
  id?: string;
  size?: 'medium' | 'large';
  color?: string;
  value?: string;
  disabled?: boolean;
  selected?: boolean;
  outOfStock?: boolean;
  multi?: boolean;
  onValue?: (value: any) => void;
  onUpdateError?: (error: Error) => void;
}

export const ColorSwatch: FunctionComponent<ColorSwatchProps> = ({
  className,
  name,
  value,
  id,
  label,
  groupAriaLabel,
  size = 'medium',
  color,
  disabled = false,
  selected = false,
  outOfStock = false,
  multi = false,
  onValue,
  onUpdateError,
  ...props
}) => {
  // i18n
  const outOfStockLabel = useText('Dropin.Swatches.outOfStock.label').label;
  const selectedLabel = useText('Dropin.Swatches.selected.label').label;
  const swatchLabel = useText('Dropin.Swatches.swatch.label').label;

  const onValueHandler = useCallback(
    async (newValue: any) => {
      if (onValue) {
        try {
          await onValue(newValue);
        } catch (e) {
          if (onUpdateError) {
            onUpdateError(e as Error);
          }
        }
      }
    },
    [onValue, onUpdateError]
  );

  const handleOnValue = (e: Event) => {
    const input = e.target as HTMLInputElement;

    onValueHandler(input.value);
  };

  const isValidColor = (color: string) => {
    const colorStyle = new Option().style;
    colorStyle.color = color;
    return colorStyle.color !== ''; // Returns true if the browser accepts this as a valid color
  };

  const defaultColor = 'var(--color-gray-300);';

  const validColor = color && isValidColor(color) ? color : defaultColor;

  const handleAriaLabel = () => {
    if (outOfStock) return `${groupAriaLabel}: ${label} ${outOfStockLabel}`;
    if (selected) return `${groupAriaLabel}: ${label} ${selectedLabel}`;
    return `${groupAriaLabel}: ${label} ${swatchLabel}`;
  };

  return (
    <label
      className={classes([
        'dropin-color-swatch__container',
        `dropin-color-swatch__container--${size}`,
        className,
      ])}
    >
      <input
        type={multi ? 'checkbox' : 'radio'}
        name={name}
        id={id}
        value={value}
        aria-label={handleAriaLabel()}
        checked={selected}
        disabled={disabled}
        onChange={handleOnValue}
        {...props}
        className={classes([
          'dropin-color-swatch',
          ['dropin-color-swatch--selected', selected],
          ['dropin-color-swatch--disabled', disabled],
          className,
        ])}
      />
      <span
        style={{ '--bg-color': validColor }}
        className={classes([
          'dropin-color-swatch__span',
          ['dropin-color-swatch__span--out-of-stock', outOfStock],
          className,
        ])}
      />
    </label>
  );
};
